import { ArticleJsonLd, NextSeo } from "next-seo";

import { formatImageUrl } from "@/components/blog/Util";
import BeforeAfter from "@/components/case-study/BeforeAfter";
import CTA from "@/components/case-study/CTA";
import Hero from "@/components/case-study/Hero";
import MoreStories from "@/components/case-study/MoreStories";
import WhySection from "@/components/case-study/WhySection";
import Footer from "@/components/marketing/pieces/Footer";
import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";
import { getAllPages } from "@/lib/contentful";

import type { ContentfulAsset } from "@/types/contentful";

export type HeaderSubheaderComponent = {
  fields: {
    header: string;
    subheader: string;
  };
};

export type CaseStudy = {
  seoTitle: string;
  seoDescription: string;
  companyName: string;
  ownerName?: string;
  title: string;
  subtitle: string;
  slug: string;
  generalReboltStat: HeaderSubheaderComponent[];
  heroImage?: ContentfulAsset;
  whatsWorkingImage?: ContentfulAsset;
  testimonialCtaImage?: ContentfulAsset;
  beforeRebolt: HeaderSubheaderComponent[];
  afterRebolt: HeaderSubheaderComponent[];
  whatsWorking: HeaderSubheaderComponent[];
  testimonial: string;
  createdAt: string;
};

const CaseStudy = ({ caseStudy, otherCaseStudies }: { caseStudy: CaseStudy; otherCaseStudies: CaseStudy[] }) => {
  return (
    <div className="bg-ash-100">
      <NextSeo
        title={caseStudy?.seoTitle}
        description={caseStudy?.seoDescription}
        canonical={`https://rebolthq.com/case-studies/${caseStudy.slug}`}
        openGraph={{
          url: `https://rebolthq.com/case-studies/${caseStudy.slug}`,
          title: caseStudy.title,
          description: caseStudy.subtitle,
          images: caseStudy.heroImage ? [{ url: formatImageUrl(caseStudy.heroImage.fields.file.url) }] : [],
        }}
      />
      <ArticleJsonLd
        useAppDir={false}
        datePublished={caseStudy.createdAt}
        url={`https://rebolthq.com/case-studies/${caseStudy.slug}`}
        title={caseStudy.title}
        description={caseStudy.subtitle}
        images={caseStudy.heroImage ? [formatImageUrl(caseStudy.heroImage.fields.file.url)] : []}
        isAccessibleForFree={true}
        authorName={caseStudy.ownerName || caseStudy.companyName}
      />

      <NavbarLegacy />

      <div className="pt-20">
        <div>
          <Hero
            title={caseStudy.title}
            subtitle={caseStudy.subtitle}
            heroImage={caseStudy.heroImage?.fields.file.url || ""}
            generalReboltStat={caseStudy.generalReboltStat}
          />
          <BeforeAfter beforeRebolt={caseStudy.beforeRebolt} afterRebolt={caseStudy.afterRebolt} />
          <WhySection
            heroImage={caseStudy.whatsWorkingImage?.fields.file.url || caseStudy.heroImage?.fields.file.url || ""}
            whatsWorking={caseStudy.whatsWorking}
          />
          <CTA
            image={caseStudy.testimonialCtaImage?.fields.file.url || caseStudy.heroImage?.fields.file.url || ""}
            testimonial={caseStudy.testimonial}
            generalReboltStat={caseStudy.generalReboltStat}
            companyName={caseStudy.companyName}
            ownerName={caseStudy.ownerName || ""}
          />
          <MoreStories caseStudies={otherCaseStudies} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export const getStaticProps = async ({ params }: { params: { slug: string } }) => {
  const { pages } = await getAllPages(1, 15, "pageCaseStudy", true);
  const caseStudy = pages.find((page: { slug: string }) => page.slug === params.slug);
  const otherCaseStudies = pages
    .filter((page: { slug: string }) => page.slug !== params.slug)
    .sort(() => Math.random() - 0.5)
    .slice(0, 2);

  if (!caseStudy) {
    return {
      notFound: true,
    };
  }
  return {
    props: {
      otherCaseStudies: otherCaseStudies,
      caseStudy,
    },
  };
};

export const getStaticPaths = async () => {
  const { pages } = await getAllPages(1, 15, "pageCaseStudy", true);

  return {
    paths: pages.map((page: { slug: string }) => {
      return {
        params: {
          slug: page.slug,
        },
      };
    }),
    fallback: false,
  };
};

export default CaseStudy;
